/**
 * Guest Cart Utilities
 * Manages cart items in localStorage for non-authenticated users
 */

const GUEST_CART_KEY = 'vesta_guest_cart';

/**
 * Get guest cart from localStorage
 * @returns {Array} Array of cart items
 */
export function getGuestCart() {
  try {
    const cartData = localStorage.getItem(GUEST_CART_KEY);
    if (!cartData) return [];
    return JSON.parse(cartData);
  } catch (error) {
    console.error('Error reading guest cart from localStorage:', error);
    return [];
  }
}

/**
 * Save guest cart to localStorage
 * @param {Array} items - Array of cart items
 */
export function saveGuestCart(items) {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving guest cart to localStorage:', error);
    // If localStorage is full or unavailable, silently fail
    // The cart will still work in memory for the session
  }
}

/**
 * Add or update item in guest cart
 * @param {Object} item - Cart item to add/update
 * @returns {Array} Updated cart items
 */
export function addToGuestCart(item) {
  const cart = getGuestCart();
  const existingIndex = cart.findIndex(i => i.id === item.id);
  
  if (existingIndex >= 0) {
    // Update existing item
    const existing = cart[existingIndex];
    const newQty = (existing.qty || 1) + (item.qty || 1);
    cart[existingIndex] = { ...existing, ...item, qty: newQty };
  } else {
    // Add new item
    cart.push({ ...item, qty: item.qty || 1 });
  }
  
  saveGuestCart(cart);
  return cart;
}

/**
 * Update item quantity in guest cart
 * @param {string} itemId - Item ID
 * @param {number} qty - New quantity
 * @returns {Array} Updated cart items
 */
export function updateGuestCartItem(itemId, qty) {
  const cart = getGuestCart();
  const itemIndex = cart.findIndex(i => i.id === itemId);
  
  if (itemIndex >= 0) {
    if (qty <= 0) {
      // Remove item if quantity is 0 or less
      cart.splice(itemIndex, 1);
    } else {
      cart[itemIndex] = { ...cart[itemIndex], qty };
    }
    saveGuestCart(cart);
  }
  
  return cart;
}

/**
 * Remove item from guest cart
 * @param {string} itemId - Item ID to remove
 * @returns {Array} Updated cart items
 */
export function removeFromGuestCart(itemId) {
  const cart = getGuestCart();
  const filtered = cart.filter(i => i.id !== itemId);
  saveGuestCart(filtered);
  return filtered;
}

/**
 * Clear guest cart
 */
export function clearGuestCart() {
  try {
    localStorage.removeItem(GUEST_CART_KEY);
  } catch (error) {
    console.error('Error clearing guest cart:', error);
  }
}

/**
 * Check if localStorage is available
 * @returns {boolean}
 */
export function isLocalStorageAvailable() {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

